import { useState } from "react";
import type { AirtimeConfig, AirtimeScheduleMode } from "../types";

export default function AirtimeConfigPanel({
  config,
  onSave,
  saving,
}: {
  config: AirtimeConfig;
  onSave: (payload: {
    scheduleMode: AirtimeScheduleMode;
    lowBalanceThreshold: number;
    enabled: boolean;
  }) => void;
  saving?: boolean;
}) {
  const [scheduleMode, setScheduleMode] = useState(config.scheduleMode);
  const [threshold, setThreshold] = useState(config.lowBalanceThreshold);
  const [enabled, setEnabled] = useState(Boolean(config.enabled));

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-black text-slate-900">
        Airtime Configuration
      </h2>

      <div className="mt-4 grid gap-4 rounded-2xl bg-slate-50 p-4 md:grid-cols-3">
        <Info label="Current Schedule" value={config.scheduleMode.replaceAll("_", " ")} />
        <Info label="Environment" value={config.currency|| "SANDBOX"} />
        <Info label="Status" value={config.enabled ? "Enabled" : "Disabled"} /> 
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">
            Schedule Mode
          </label>

          <select
            value={scheduleMode}
            onChange={(e) => setScheduleMode(e.target.value as AirtimeScheduleMode)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3"
          >
            <option value="FIRST_DAY_OF_MONTH">First Day of Month</option>
            <option value="FIRST_MONDAY_OF_MONTH">First Monday of Month</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">
            Low Balance Threshold
          </label>

          <input
            type="number"
            min={0}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3"
          />
        </div>
      </div>

      <label className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 p-4">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />

        <span className="text-sm font-semibold text-slate-700">
          Enable Airtime Disbursement
        </span>
      </label>

      <button
        disabled={saving}
        onClick={() =>
          onSave({
            scheduleMode,
            lowBalanceThreshold: threshold,
            enabled,
          })
        }
        className="mt-6 rounded-2xl bg-emerald-600 px-6 py-3 font-black text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Configuration"}
      </button>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-black text-slate-900">{value}</p>
    </div>
  );
}